export const SESSION_SLOT_DEFAULT_CAPACITY: Record<string, number> = {
  hourly: 6,
  two_hour: 12,
};

export function getDefaultSessionCapacity(sessionSlotType?: string): number {
  if (sessionSlotType === "two_hour") return SESSION_SLOT_DEFAULT_CAPACITY.two_hour;
  return SESSION_SLOT_DEFAULT_CAPACITY.hourly;
}

export const SESSION_SLOT_INCREMENTS: Record<string, number> = {
  half_hour: 30,
  hourly: 60,
  two_hour: 120,
};

export function getSessionSlotIncrementMinutes(sessionSlotType?: string): number | null {
  if (!sessionSlotType) return SESSION_SLOT_INCREMENTS.hourly;
  return SESSION_SLOT_INCREMENTS[sessionSlotType] ?? null;
}

export function getSessionSlotTypeLabel(sessionSlotType?: string): string {
  switch (sessionSlotType) {
    case "hourly":
      return "Hourly";
    case "half_hour":
      return "30 Minutes";
    case "two_hour":
      return "2 Hours";
    default:
      return sessionSlotType || "Custom";
  }
}

export function buildTimeSlotRanges(
  startTime: string,
  endTime: string,
  incrementMinutes: number
): string[] {
  const pad = (n: number) => String(n).padStart(2, "0");
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  const startMinutes = sh * 60 + (sm || 0);
  const endMinutes = eh * 60 + (em || 0);
  const slots: string[] = [];

  for (let slotStart = startMinutes; slotStart + incrementMinutes <= endMinutes; slotStart += incrementMinutes) {
    const slotEnd = slotStart + incrementMinutes;
    slots.push(
      `${pad(Math.floor(slotStart / 60))}:${pad(slotStart % 60)}-${pad(Math.floor(slotEnd / 60))}:${pad(slotEnd % 60)}`
    );
  }

  return slots;
}

export function detectIncrementFromSlotCapacities(
  slotCapacities: Record<string, number> | null | undefined,
  dayKey: string
): number | null {
  if (!slotCapacities) return null;

  const keys = Object.keys(slotCapacities);
  const dayKeys = keys.filter((k) => k.startsWith(`${dayKey}_`));
  const inspectKeys = dayKeys.length ? dayKeys : keys;

  for (const key of inspectKeys) {
    const raw = key.includes("_") ? key.split("_")[1] : key;
    const [a, b] = raw.split("-");
    const [ah, am] = (a || "").split(":").map(Number);
    const [bh, bm] = (b || "").split(":").map(Number);
    if (isNaN(ah) || isNaN(am) || isNaN(bh) || isNaN(bm)) continue;

    const duration = bh * 60 + bm - (ah * 60 + am);
    if (duration === 30 || duration === 60 || duration === 120) {
      return duration;
    }
  }

  return null;
}

type SessionAgeGroupSource = {
  ageGroups?: string[] | null;
  slotAgeGroups?: Record<string, string[]> | null;
};

const timeToMinutes = (hm: string): number => {
  const [h, m] = hm.split(":").map(Number);
  return h * 60 + (m || 0);
};

export function getAgeGroupsForSlot(session: SessionAgeGroupSource, slot: string): string[] {
  const baseGroups: string[] = session?.ageGroups || [];
  const slotAgeGroups = session?.slotAgeGroups;
  if (!slotAgeGroups || !slot) return baseGroups;

  const [selStartStr, selEndStr] = slot.split("-") as [string, string];
  if (!selStartStr || !selEndStr) return baseGroups;

  const selStart = timeToMinutes(selStartStr);
  const selEnd = timeToMinutes(selEndStr);

  for (const [key, arr] of Object.entries(slotAgeGroups)) {
    const [cfgStartStr, cfgEndStr] = key.split("-") as [string, string];
    if (!cfgStartStr || !cfgEndStr) continue;
    const cfgStart = timeToMinutes(cfgStartStr);
    const cfgEnd = timeToMinutes(cfgEndStr);
    if (selStart >= cfgStart && selEnd <= cfgEnd) {
      return arr?.length ? arr : baseGroups;
    }
  }

  return baseGroups;
}

export function getAvailableAgeGroupsForSlots(
  session: SessionAgeGroupSource,
  slots: string[]
): string[] {
  const groups = new Set<string>();
  for (const slot of slots) {
    for (const group of getAgeGroupsForSlot(session, slot)) {
      groups.add(group);
    }
  }
  if (groups.size === 0) {
    for (const group of session?.ageGroups || []) groups.add(group);
  }
  return Array.from(groups);
}

export function filterSlotsByAgeGroup(
  session: SessionAgeGroupSource,
  slots: string[],
  ageGroup: string
): string[] {
  if (!ageGroup) return [];
  return slots.filter((slot) => getAgeGroupsForSlot(session, slot).includes(ageGroup));
}
